import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@vaiu/ui/card';
import { Button } from '@vaiu/ui/button';
import { Input } from '@vaiu/ui/input';
import { Label } from '@vaiu/ui/label';
import { Badge } from '@vaiu/ui/badge';
import { Separator } from '@vaiu/ui/separator';
import { Switch } from '@vaiu/ui/switch';
import { ArrowLeft, Building, Edit, Save, Wallet, X } from 'lucide-react';
import { toast } from 'sonner';
import { i18n } from '@/utils/i18n';
import { platformApi } from '@/services/platformApi';
import { isPayPalPayoutDestinationReady } from '@vaiu/utils';

export type ProfileTransferOutPayoutsProfile = {
  roles: string[];
  profile: {
    paypalEmail?: string;
    paypalEmailVerified?: boolean;
    transferOutPreferences?: Record<string, { enabled?: boolean }>;
    stripeConnectId?: string;
  };
};

export type ProfileTransferOutPayoutsCardProps = {
  profile: ProfileTransferOutPayoutsProfile;
  editing: boolean;
  onEditingChange: (editing: boolean) => void;
  onRefresh: () => Promise<void>;
  /** Runs after successful PayPal code verification (e.g. refresh auth JWT). */
  onAfterPaypalVerify?: () => Promise<void>;
  /** When true, always show the edit form and hide the header edit/cancel affordances that collapse the card. */
  hideEditToggle?: boolean;
  cardId?: string;
  className?: string;
};

export function ProfileTransferOutPayoutsCard({
  profile,
  editing,
  onEditingChange,
  onRefresh,
  onAfterPaypalVerify,
  hideEditToggle = false,
  cardId = 'transfer-out',
  className,
}: ProfileTransferOutPayoutsCardProps) {
  const [payoutDraft, setPayoutDraft] = useState<{
    paypalEmail: string;
    paymentMethodPreferences: Record<string, { enabled?: boolean }>;
  }>({ paypalEmail: '', paymentMethodPreferences: {} });
  const [paypalVerificationCode, setPaypalVerificationCode] = useState('');
  const [sendingPaypalCode, setSendingPaypalCode] = useState(false);
  const [verifyingPaypalCode, setVerifyingPaypalCode] = useState(false);

  const showEditForm = hideEditToggle || editing;

  useEffect(() => {
    if (!editing || !profile) return;
    setPayoutDraft({
      paypalEmail: profile.profile.paypalEmail || '',
      paymentMethodPreferences: { ...(profile.profile.transferOutPreferences || {}) },
    });
    setPaypalVerificationCode('');
  }, [editing]);

  useEffect(() => {
    if (!editing || !profile) return;
    setPayoutDraft((prev) => ({
      ...prev,
      paypalEmail: profile.profile.paypalEmail || prev.paypalEmail || '',
    }));
  }, [editing, profile.profile.paypalEmail, profile.profile.paypalEmailVerified]);

  const paypalEmailsMatch = useMemo(() => {
    const a = (payoutDraft.paypalEmail || '').trim().toLowerCase();
    const b = (profile?.profile?.paypalEmail || '').trim().toLowerCase();
    return a === b && a.length > 0;
  }, [payoutDraft.paypalEmail, profile?.profile?.paypalEmail]);

  const paypalVerifiedForProfile = useMemo(
    () =>
      isPayPalPayoutDestinationReady({
        paypalEmail: profile?.profile?.paypalEmail,
        paypalEmailVerified: profile?.profile?.paypalEmailVerified,
      }),
    [profile?.profile?.paypalEmail, profile?.profile?.paypalEmailVerified]
  );

  const canEnablePaypalToggle = paypalEmailsMatch && paypalVerifiedForProfile;

  /** Saved email is verified and matches the field — hide code UX until the user edits the email. */
  const showPaypalVerificationControls =
    !paypalEmailsMatch || !paypalVerifiedForProfile;

  useEffect(() => {
    if (paypalEmailsMatch && paypalVerifiedForProfile) {
      setPaypalVerificationCode('');
    }
  }, [paypalEmailsMatch, paypalVerifiedForProfile]);

  const updateTransferOutPreferences = async () => {
    try {
      await platformApi.updatePreferences('transfer-out', {
        transferOutPreferences: payoutDraft.paymentMethodPreferences,
      });
      await onRefresh();
      if (!hideEditToggle) {
        onEditingChange(false);
        setPaypalVerificationCode('');
      }
      toast.success(i18n.__('profile.toast.transferOutUpdated'));
    } catch (error) {
      console.error('Failed to update transfer out preferences:', error);
      toast.error(i18n.__('profile.toast.transferOutFailed'));
    }
  };

  const sendPayPalVerificationCode = async () => {
    const email = (payoutDraft.paypalEmail || '').trim();
    if (!email) {
      toast.error('Enter a PayPal email address first');
      return;
    }
    setSendingPaypalCode(true);
    try {
      await platformApi.sendPayPalPayoutEmailVerification(email);
      toast.success(i18n.__('profile.toast.paypalVerificationSent'));
    } catch (error: unknown) {
      console.error(error);
      const msg = error instanceof Error ? error.message : undefined;
      toast.error(msg || i18n.__('profile.toast.paypalVerificationSendFailed'));
    } finally {
      setSendingPaypalCode(false);
    }
  };

  const verifyPayPalVerificationCode = async () => {
    const code = paypalVerificationCode.trim();
    if (!code) {
      toast.error('Enter the verification code from your email');
      return;
    }
    setVerifyingPaypalCode(true);
    try {
      await platformApi.verifyPayPalPayoutEmail(code);
      setPaypalVerificationCode('');
      await onRefresh();
      await onAfterPaypalVerify?.();
      toast.success(i18n.__('profile.toast.paypalVerified'));
    } catch (error: unknown) {
      console.error(error);
      const msg = error instanceof Error ? error.message : undefined;
      toast.error(msg || i18n.__('profile.toast.paypalVerifyFailed'));
    } finally {
      setVerifyingPaypalCode(false);
    }
  };

  return (
    <Card id={hideEditToggle ? undefined : cardId} className={className}>
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center space-x-2">
          <ArrowLeft className="w-5 h-5 transform rotate-180" />
          <CardTitle>{i18n.__('vaiu.profile_page.transfer_out.payouts_title')}</CardTitle>
        </div>
        {!hideEditToggle && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEditingChange(!editing)}
          >
            {editing ? <X className="w-4 h-4" /> : <Edit className="w-4 h-4" />}
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <CardDescription>
          {i18n.__('vaiu.profile_page.transfer_out.payouts_intro')}
        </CardDescription>

        {showEditForm ? (
          <div className="space-y-6">
            <div className="space-y-3 rounded-lg border p-4 bg-muted/30">
              <Label htmlFor="paypalEmail" className="text-base font-medium">
                {i18n.__('vaiu.profile_page.transfer_out.paypal_email_label')}
              </Label>
              <Input
                id="paypalEmail"
                type="email"
                autoComplete="email"
                value={payoutDraft.paypalEmail || ''}
                onChange={(e) =>
                  setPayoutDraft({ ...payoutDraft, paypalEmail: e.target.value })
                }
                placeholder="your-paypal@example.com"
                className="max-w-md"
              />
              <div className="flex flex-wrap items-center gap-2">
                {showPaypalVerificationControls && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={sendPayPalVerificationCode}
                    disabled={sendingPaypalCode}
                  >
                    {sendingPaypalCode ? '…' : i18n.__('vaiu.profile_page.transfer_out.send_code')}
                  </Button>
                )}
                {profile.profile.paypalEmail && (
                  <Badge variant={paypalVerifiedForProfile && paypalEmailsMatch ? 'success' : 'warning'}>
                    {paypalVerifiedForProfile && paypalEmailsMatch
                      ? i18n.__('vaiu.profile_page.transfer_out.verified')
                      : i18n.__('vaiu.profile_page.transfer_out.not_verified')}
                  </Badge>
                )}
              </div>
              {showPaypalVerificationControls && (
                <div className="flex flex-col sm:flex-row sm:items-end gap-2 max-w-md">
                  <div className="flex-1 space-y-1">
                    <Label htmlFor="paypalVerifyCode" className="text-sm">
                      {i18n.__('vaiu.profile_page.transfer_out.code_label')}
                    </Label>
                    <Input
                      id="paypalVerifyCode"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={8}
                      value={paypalVerificationCode}
                      onChange={(e) =>
                        setPaypalVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 8))
                      }
                      placeholder="000000"
                      className="tracking-widest font-mono"
                    />
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    onClick={verifyPayPalVerificationCode}
                    disabled={verifyingPaypalCode || paypalVerificationCode.length < 4}
                  >
                    {verifyingPaypalCode ? '…' : i18n.__('vaiu.profile_page.transfer_out.verify')}
                  </Button>
                </div>
              )}
            </div>

            <Separator />

            <div className="space-y-4">
              <div>
                <Label className="text-base font-medium">
                  {i18n.__('vaiu.profile_page.transfer_out.enabled_methods')}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {i18n.__('vaiu.profile_page.transfer_out.enabled_methods_desc')}
                </p>
              </div>

              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center space-x-3">
                  <Wallet className="w-5 h-5 text-blue-600" />
                  <div>
                    <Label className="text-base font-medium">
                      {i18n.__('vaiu.profile_page.transfer_out.paypal')}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      {i18n.__('vaiu.profile_page.transfer_out.paypal_desc')}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={payoutDraft.paymentMethodPreferences?.paypal?.enabled || false}
                  onCheckedChange={(checked) => {
                    if (checked && !canEnablePaypalToggle) {
                      toast.error(i18n.__('vaiu.profile_page.transfer_out.toggle_requires_verify'));
                      return;
                    }
                    setPayoutDraft((prev) => ({
                      ...prev,
                      paymentMethodPreferences: {
                        ...prev.paymentMethodPreferences,
                        paypal: { enabled: checked },
                      },
                    }));
                  }}
                />
              </div>
              {!canEnablePaypalToggle && (
                <p className="text-xs text-muted-foreground">
                  {i18n.__('vaiu.profile_page.transfer_out.toggle_requires_verify')}
                </p>
              )}

              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center space-x-3">
                  <Building className="w-5 h-5 text-green-600" />
                  <div>
                    <Label className="text-base font-medium">
                      {i18n.__('vaiu.profile_page.transfer_out.bank_accounts')}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      {i18n.__('vaiu.profile_page.transfer_out.bank_accounts_desc')}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={payoutDraft.paymentMethodPreferences?.bank?.enabled || false}
                  onCheckedChange={(checked) =>
                    setPayoutDraft((prev) => ({
                      ...prev,
                      paymentMethodPreferences: {
                        ...prev.paymentMethodPreferences,
                        bank: { enabled: checked },
                      },
                    }))
                  }
                />
              </div>

              {profile.roles.includes('MERCHANT') && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    <Label className="text-base font-medium">
                      {i18n.__('vaiu.profile_page.transfer_out.merchant_info')}
                    </Label>
                    <div className="p-3 bg-muted rounded-lg">
                      <div className="flex items-center space-x-2">
                        <Building className="w-4 h-4" />
                        <Label className="text-sm font-medium">
                          {i18n.__('vaiu.profile_page.transfer_out.stripe_connect')}
                        </Label>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {profile.profile.stripeConnectId
                          ? i18n.__('vaiu.profile_page.transfer_out.stripe_connected', {
                              id: profile.profile.stripeConnectId,
                            })
                          : i18n.__('vaiu.profile_page.transfer_out.stripe_not_connected')}
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              <Button onClick={updateTransferOutPreferences} size="sm">
                <Save className="w-4 h-4 mr-2" />
                {i18n.__('common.save') || 'Save'}
              </Button>
              {!hideEditToggle && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    onEditingChange(false);
                    setPaypalVerificationCode('');
                  }}
                >
                  {i18n.__('common.cancel')}
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-3 p-3 border rounded-lg">
              <div className="flex min-w-0 items-start space-x-3">
                <Wallet className="w-5 h-5 shrink-0 text-blue-600" />
                <div className="min-w-0">
                  <Label className="text-base font-medium">
                    {i18n.__('vaiu.profile_page.transfer_out.paypal_email_label')}
                  </Label>
                  <p className="text-sm text-muted-foreground break-words">
                    {profile.profile.paypalEmail || i18n.__('vaiu.profile_page.transfer_out.not_set')}
                  </p>
                </div>
              </div>
              <div className="flex flex-row flex-wrap items-center justify-end gap-1.5 shrink-0 pt-0.5">
                <Badge variant={profile.profile.paypalEmail ? 'info' : 'muted'}>
                  {profile.profile.paypalEmail
                    ? i18n.__('vaiu.profile_page.transfer_out.configured')
                    : i18n.__('vaiu.profile_page.transfer_out.not_configured')}
                </Badge>
                {profile.profile.paypalEmail && (
                  <Badge variant={profile.profile.paypalEmailVerified === false ? 'warning' : 'success'}>
                    {profile.profile.paypalEmailVerified === false
                      ? i18n.__('vaiu.profile_page.transfer_out.not_verified')
                      : i18n.__('vaiu.profile_page.transfer_out.verified')}
                  </Badge>
                )}
              </div>
            </div>

            <div className="space-y-3">
              <Label className="text-base font-medium">
                {i18n.__('vaiu.profile_page.transfer_out.enabled_methods')}
              </Label>

              <div className="flex items-center justify-between gap-3 p-3 border rounded-lg">
                <div className="flex min-w-0 items-center space-x-3">
                  <Wallet className="w-5 h-5 shrink-0 text-blue-600" />
                  <div className="min-w-0">
                    <Label className="text-base font-medium">
                      {i18n.__('vaiu.profile_page.transfer_out.paypal')}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      {i18n.__('vaiu.profile_page.transfer_out.paypal_desc')}
                    </p>
                  </div>
                </div>
                <Badge
                  variant={profile.profile.transferOutPreferences?.paypal?.enabled ? 'success' : 'muted'}
                  className="shrink-0"
                >
                  {profile.profile.transferOutPreferences?.paypal?.enabled
                    ? i18n.__('vaiu.profile_page.transfer_out.enabled')
                    : i18n.__('vaiu.profile_page.transfer_out.disabled')}
                </Badge>
              </div>

              <div className="flex items-center justify-between gap-3 p-3 border rounded-lg">
                <div className="flex min-w-0 items-center space-x-3">
                  <Building className="w-5 h-5 shrink-0 text-green-600" />
                  <div className="min-w-0">
                    <Label className="text-base font-medium">
                      {i18n.__('vaiu.profile_page.transfer_out.bank_accounts')}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      {i18n.__('vaiu.profile_page.transfer_out.bank_accounts_desc')}
                    </p>
                  </div>
                </div>
                <Badge
                  variant={profile.profile.transferOutPreferences?.bank?.enabled ? 'success' : 'muted'}
                  className="shrink-0"
                >
                  {profile.profile.transferOutPreferences?.bank?.enabled
                    ? i18n.__('vaiu.profile_page.transfer_out.enabled')
                    : i18n.__('vaiu.profile_page.transfer_out.disabled')}
                </Badge>
              </div>
            </div>

            {profile.roles.includes('MERCHANT') && (
              <div className="p-3 bg-muted rounded-lg">
                <div className="flex items-center space-x-2">
                  <Building className="w-4 h-4" />
                  <Label className="text-sm font-medium">
                    {i18n.__('vaiu.profile_page.transfer_out.stripe_connect')}
                  </Label>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {profile.profile.stripeConnectId
                    ? i18n.__('vaiu.profile_page.transfer_out.stripe_connected', {
                        id: profile.profile.stripeConnectId,
                      })
                    : i18n.__('vaiu.profile_page.transfer_out.stripe_not_connected')}
                </p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
